import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CsvInput from "./CsvInput";

const EMIRATES = ["Dubai", "Abu Dhabi", "Sharjah", "Ras Al Khaimah", "Ajman", "Fujairah", "Umm Al Quwain"];
const PUBLISH = ["draft", "review", "published"];

const empty = {
  slug: "", name: "", emirate: "", emirates_of_operation: [],
  headquarters: "", year_established: "", website: "", description: "",
  logo: "", hero_image: "", cover_image: "",
  awards: [], contact_email: "", contact_phone: "",
  seo_title: "", seo_description: "", publish_status: "published",
  strengths: [], signature_properties: [],
  best_suited_for: "", not_ideal_for: "", verdict: "",
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.2em] text-gray-500 block mb-2">{label}</span>
      {children}
    </label>
  );
}

export default function AdminDeveloperForm() {
  const { slug } = useParams();
  const isEdit = Boolean(slug);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isEdit) {
      api.get(`/developers/${slug}`).then((r) => {
        const d = r.data;
        setForm({
          ...empty,
          ...d,
          year_established: d.year_established ?? d.founded ?? "",
          emirates_of_operation: d.emirates_of_operation && d.emirates_of_operation.length ? d.emirates_of_operation : [d.emirate || "Dubai"],
        });
      });
    }
  }, [slug, isEdit]);

  const update = (k, v) => setForm((s) => {
    // When the admin picks a primary emirate on a fresh create, auto-populate
    // "Emirates of operation" if they haven't touched it yet — prevents the
    // "created dev only shows in Dubai" bug where the primary emirate and the
    // operations list drift out of sync.
    if (k === "emirate" && v && !isEdit) {
      const ops = s.emirates_of_operation || [];
      if (ops.length === 0 || (ops.length === 1 && ops[0] === "Dubai" && !ops.includes(v))) {
        return { ...s, emirate: v, emirates_of_operation: [v] };
      }
    }
    return { ...s, [k]: v };
  });

  const submit = async (e) => {
    e.preventDefault();
    return submitWithStatus(form.publish_status || "published");
  };

  const submitWithStatus = async (status) => {
    if (!form.emirate) {
      toast.error("Please choose a primary emirate before saving.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        publish_status: status,
        slug: form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        year_established: form.year_established ? Number(form.year_established) : null,
        emirates_of_operation: (form.emirates_of_operation && form.emirates_of_operation.length
          ? Array.from(new Set([form.emirate, ...form.emirates_of_operation]))
          : [form.emirate]),
      };
      if (isEdit) {
        await api.put(`/admin/developers/${slug}`, payload);
        toast.success(status === "published" ? "Developer published" : "Draft saved");
      } else {
        await api.post(`/admin/developers`, payload);
        toast.success(status === "published" ? "Developer published" : "Draft created");
      }
      setForm((s) => ({ ...s, publish_status: status }));
      navigate("/admin/developers");
    } catch (err) {
      const raw = err?.response?.data?.detail;
      let msg = "Save failed";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw)) msg = raw.map((e) => `${(e.loc || []).slice(-1)}: ${e.msg}`).join(" · ");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const uploadLogo = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a file"); return; }
    if (!isEdit) { toast.error("Save the developer first, then upload a logo."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/developers/${slug}/logo`, fd);
      update("logo", data.logo);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const resolveLogo = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${url}`;
  };

  const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-[#001588]";

  const statusPill = form.publish_status === "published"
    ? { label: "Live", cls: "bg-green-100 text-green-800 border-green-300" }
    : form.publish_status === "review"
      ? { label: "Under review", cls: "bg-amber-100 text-amber-800 border-amber-300" }
      : { label: "Draft", cls: "bg-neutral-100 text-neutral-700 border-neutral-300" };

  return (
    <form onSubmit={submit} className="p-10 max-w-5xl" data-testid="admin-developer-form">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Directory</div>
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-4xl">{isEdit ? "Edit developer" : "New developer"}</h1>
            <span className={`text-xs uppercase tracking-[0.15em] px-2.5 py-1 border ${statusPill.cls}`} data-testid="dev-status-pill">
              {statusPill.label}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => submitWithStatus("draft")} disabled={saving} data-testid="dev-save-draft-btn" className="px-5 py-3 border border-[#001588] text-[#001588] text-sm uppercase tracking-[0.2em] hover:bg-[#001588] hover:text-white transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save as draft"}
          </button>
          <button type="button" onClick={() => submitWithStatus("published")} disabled={saving} data-testid="dev-publish-btn" className="px-6 py-3 bg-[#001588] text-white text-sm uppercase tracking-[0.2em] hover:bg-black disabled:opacity-60">
            {saving ? "Publishing…" : form.publish_status === "published" && isEdit ? "Update live" : "Publish"}
          </button>
        </div>
      </div>

      {isEdit && (
        <div className="bg-white border border-[#E5E7EB] p-6 mb-6" data-testid="dev-logo-section">
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Brand logo</div>
          <div className="flex items-center gap-6">
            <div className="w-32 h-32 border border-[#E5E7EB] bg-white flex items-center justify-center overflow-hidden">
              {form.logo ? (
                <img src={resolveLogo(form.logo)} alt={form.name} className="max-w-full max-h-full object-contain p-3" data-testid="dev-current-logo" />
              ) : (
                <span className="text-xs text-gray-400 text-center">No logo</span>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-3">Upload a PNG, JPG, WebP or SVG. Replaces the current logo across the entire website.</p>
              <div className="flex items-center gap-3">
                <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="text-sm" data-testid="dev-logo-file" />
                <button type="button" onClick={uploadLogo} disabled={uploading} data-testid="dev-logo-upload-btn" className="px-4 py-2 bg-[#001588] text-white text-xs uppercase tracking-[0.2em] hover:bg-black disabled:opacity-60">
                  {uploading ? "Uploading…" : "Upload logo"}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-3">Or paste a hosted URL:</p>
              <input className={`${inputCls} mt-1`} placeholder="https://…/logo.png" value={form.logo || ""} onChange={(e) => update("logo", e.target.value)} data-testid="dev-logo-url" />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white border border-[#E5E7EB] p-8">
        <Field label="Developer name *"><input required className={inputCls} value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="dev-input-name" /></Field>
        <Field label="Slug"><input className={inputCls} value={form.slug} onChange={(e) => update("slug", e.target.value)} disabled={isEdit} data-testid="dev-input-slug" /></Field>

        <Field label="Primary emirate *">
          <select
            required
            className={inputCls}
            value={form.emirate || ""}
            onChange={(ev) => update("emirate", ev.target.value)}
            data-testid="dev-input-emirate"
          >
            <option value="" disabled>— Choose emirate —</option>
            {EMIRATES.map((em) => <option key={em} value={em}>{em}</option>)}
          </select>
        </Field>
        <Field label="Year established"><input type="number" className={inputCls} value={form.year_established} onChange={(e) => update("year_established", e.target.value)} /></Field>

        <div className="md:col-span-2">
          <Field label="Emirates of operation (comma-separated)"><CsvInput className={inputCls} value={form.emirates_of_operation} onChange={(v) => update("emirates_of_operation", v)} data-testid="developer-input-emirates" /></Field>
        </div>

        <Field label="Headquarters"><input className={inputCls} value={form.headquarters || ""} onChange={(e) => update("headquarters", e.target.value)} placeholder="Emaar Square, Dubai" /></Field>
        <Field label="Official website"><input className={inputCls} value={form.website || ""} onChange={(e) => update("website", e.target.value)} placeholder="https://…" /></Field>

        <div className="md:col-span-2">
          <Field label="Description"><textarea rows={5} className={inputCls} value={form.description || ""} onChange={(e) => update("description", e.target.value)} /></Field>
        </div>

        <Field label="Hero image URL (optional)"><input className={inputCls} value={form.hero_image || ""} onChange={(e) => update("hero_image", e.target.value)} /></Field>
        <Field label="Cover image URL (optional)"><input className={inputCls} value={form.cover_image || ""} onChange={(e) => update("cover_image", e.target.value)} /></Field>

        <Field label="Contact email"><input className={inputCls} value={form.contact_email || ""} onChange={(e) => update("contact_email", e.target.value)} /></Field>
        <Field label="Contact phone"><input className={inputCls} value={form.contact_phone || ""} onChange={(e) => update("contact_phone", e.target.value)} /></Field>

        <div className="md:col-span-2">
          <Field label="Awards (comma-separated)"><CsvInput className={inputCls} value={form.awards} onChange={(v) => update("awards", v)} data-testid="developer-input-awards" /></Field>
        </div>

        {/* Structured editorial sections — surface on the public detail page */}
        <div className="md:col-span-2 mt-4 pt-4 border-t border-neutral-200">
          <div className="text-xs uppercase tracking-[0.24em] text-neutral-500 mb-3">Editorial sections (shown on public detail page)</div>
        </div>
        <div className="md:col-span-2">
          <Field label="Strengths (one per line, or comma-separated)">
            <CsvInput className={inputCls} value={form.strengths} onChange={(v) => update("strengths", v)} data-testid="developer-input-strengths" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Signature properties (one per line, or comma-separated)">
            <CsvInput className={inputCls} value={form.signature_properties} onChange={(v) => update("signature_properties", v)} data-testid="developer-input-signature" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Best suited for">
            <textarea rows={3} className={inputCls} value={form.best_suited_for || ""} onChange={(e) => update("best_suited_for", e.target.value)} data-testid="developer-input-best-suited" placeholder="e.g. First-time buyers seeking affordable entry points with flexible payment plans." />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="May not be ideal for">
            <textarea rows={3} className={inputCls} value={form.not_ideal_for || ""} onChange={(e) => update("not_ideal_for", e.target.value)} data-testid="developer-input-not-ideal" placeholder="e.g. Ultra-luxury buyers looking for branded residences or waterfront villas." />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Point Penta verdict">
            <textarea rows={4} className={inputCls} value={form.verdict || ""} onChange={(e) => update("verdict", e.target.value)} data-testid="developer-input-verdict" placeholder="Our advisor's one-paragraph take on this developer." />
          </Field>
        </div>

        <Field label="SEO title"><input className={inputCls} value={form.seo_title || ""} onChange={(e) => update("seo_title", e.target.value)} /></Field>
        <Field label="SEO description"><input className={inputCls} value={form.seo_description || ""} onChange={(e) => update("seo_description", e.target.value)} /></Field>

        <Field label="Publish status">
          <select className={inputCls} value={form.publish_status} onChange={(e) => update("publish_status", e.target.value)} data-testid="dev-input-publish">
            {PUBLISH.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
    </form>
  );
}
